/**
 * Utility functions for generating PDF export options for TinyMCE
 * strictly following APA 7th Edition guidelines and institutional overrides.
 */

/** Header/footer slot values */
export const HEADER_SLOT = {
	NONE: 'none',
	PAGE_NUMBER: 'pageNumber',
	RUNNING_HEAD: 'runningHead',
	DOCUMENT_TITLE: 'documentTitle',
};

/** Legacy TinyMCE value — treated as running head */
export function normalizeHeaderSlot(value) {
	if (!value || value === 'none') return HEADER_SLOT.NONE;
	if (value === 'title') return HEADER_SLOT.RUNNING_HEAD;
	return value;
}

/**
 * Generates an abbreviated running head from the title (max 50 chars).
 * @param {string} title
 * @returns {string}
 */
export const getRunningHead = (title) => {
	if (!title) return 'UNTITLED';

	const upperTitle = title.toUpperCase();

	if (upperTitle.length <= 50) {
		return upperTitle;
	}

	let truncated = upperTitle.slice(0, 50);

	if (upperTitle[50] !== ' ' && truncated.lastIndexOf(' ') > -1) {
		truncated = truncated.slice(0, truncated.lastIndexOf(' '));
	}

	return truncated.replace(/[.,;:\s]+$/, '');
};

/**
 * @param {string} slot
 * @param {{ runningHead: string, documentTitle?: string }} texts
 * @returns {string}
 */
export function resolveHeaderSlotText(slot, { runningHead, documentTitle = '' }) {
	const normalized = normalizeHeaderSlot(slot);
	if (normalized === HEADER_SLOT.RUNNING_HEAD) return runningHead;
	if (normalized === HEADER_SLOT.DOCUMENT_TITLE) {
		return (documentTitle || runningHead || '').trim();
	}
	return '';
}

function isChapterOneSection(title = '') {
	const t = String(title).toLowerCase();
	return (
		/(^|\s)chapter\s*1(\s|:|$)/.test(t) ||
		(t.includes('chapter 1') && t.includes('introduction'))
	);
}

function isFrontMatterCategory(name = '') {
	const n = String(name).toLowerCase();
	return (
		n.includes('front matter') ||
		n.includes('preliminary') ||
		n.includes('front-matter')
	);
}

/**
 * Estimate how many PDF pages should use Roman numerals before Arabic restarts at 1.
 * Uses configured value when set; otherwise counts front-matter sections for thesis/dissertation.
 * @param {string} documentTypeName
 * @param {{ categories?: Array }} [fileContent]
 * @param {number|string} [configuredCount]
 * @returns {number}
 */
export function resolveRomanNumeralFrontPages(documentTypeName, fileContent, configuredCount) {
	const configured = Number(configuredCount);
	if (Number.isFinite(configured) && configured > 0) {
		return Math.min(500, Math.floor(configured));
	}

	const type = (documentTypeName || '').toLowerCase();
	if (!/dissertation|thesis/.test(type)) return 0;

	let count = 0;
	let reachedBody = false;

	for (const category of fileContent?.categories || []) {
		if (reachedBody) break;
		const inFrontMatter = isFrontMatterCategory(category?.name);

		for (const section of category?.sections || []) {
			if (section?.isVisible === false) continue;
			if (section?.optional === true && section?.required === false) continue;

			if (isChapterOneSection(section?.title)) {
				reachedBody = true;
				break;
			}

			const catName = (category?.name || '').toLowerCase();
			if (inFrontMatter || !catName.includes('chapter')) {
				count += 1;
			} else if (!/chapter\s*[2-9]/.test(catName)) {
				// First non-front-matter category before chapter 1 still counts as front matter
				count += 1;
			}
		}
	}

	return Math.min(500, Math.max(count, 0));
}

const generateSectionHtml = (config, runHead, type = 'header', documentTitle = '') => {
	if (!config || (config.left === 'none' && config.right === 'none')) {
		return null;
	}

	const verticalMargin =
		type === 'header' ? 'margin-top: 0.5in; margin-bottom: 0;' : 'margin-top: 0; margin-bottom: 0.5in;';

	const containerStyle = `width: calc(100% - 2in); margin-left: 1in; margin-right: 1in; ${verticalMargin} font-family: "Times New Roman", Times, serif; font-size: 12pt; border: none; border-collapse: collapse;`;
	const leftCellStyle = 'text-align: left; vertical-align: top; width: 50%;';
	const rightCellStyle = 'text-align: right; vertical-align: top; width: 50%;';

	const getContent = (contentType) => {
		const slot = normalizeHeaderSlot(contentType);
		if (slot === HEADER_SLOT.RUNNING_HEAD) {
			return `<span style="text-transform: uppercase;">${runHead}</span>`;
		}
		if (slot === HEADER_SLOT.DOCUMENT_TITLE) {
			const titleText = (documentTitle || runHead || '').trim();
			return titleText ? `<span>${titleText}</span>` : '';
		}
		if (slot === HEADER_SLOT.PAGE_NUMBER) {
			return '<span class="pageNumber"></span>';
		}
		return '';
	};

	return `
        <table style="${containerStyle}">
            <tr>
                <td style="${leftCellStyle}">${getContent(config.left)}</td>
                <td style="${rightCellStyle}">${getContent(config.right)}</td>
            </tr>
        </table>
    `;
};

/**
 * @param {string} documentTypeName
 * @returns {{ header: { left: string, right: string }, footer: { left: string, right: string } }}
 */
export const getDefaultExportConfig = (documentTypeName) => {
	const type = documentTypeName?.toLowerCase() || '';

	// Professional / manuscript papers — running head + page number (APA)
	if (
		type.includes('manuscript') ||
		(type.includes('academic') && type.includes('professional')) ||
		(type.includes('professional') && type.includes('paper'))
	) {
		return {
			header: { left: HEADER_SLOT.RUNNING_HEAD, right: HEADER_SLOT.PAGE_NUMBER },
			footer: { left: HEADER_SLOT.NONE, right: HEADER_SLOT.NONE },
		};
	}

	// Thesis / dissertation — page numbers only by default (no running head)
	if (type.includes('dissertation') || type.includes('thesis')) {
		return {
			header: { left: HEADER_SLOT.NONE, right: HEADER_SLOT.PAGE_NUMBER },
			footer: { left: HEADER_SLOT.NONE, right: HEADER_SLOT.NONE },
		};
	}

	// Student papers — page numbers only
	return {
		header: { left: HEADER_SLOT.NONE, right: HEADER_SLOT.PAGE_NUMBER },
		footer: { left: HEADER_SLOT.NONE, right: HEADER_SLOT.NONE },
	};
};

const generateWatermarkHtml = () => `
        <div style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: -1;">
            <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-45deg); font-size: 80pt; font-family: Arial, sans-serif; color: rgba(0,0,0,0.06); white-space: nowrap; font-weight: 600;">WriteForm</div>
        </div>
    `;

export const getExportPdfOptions = (
	title,
	documentTypeName = 'Academic Paper',
	customConfig = null,
	opts = {}
) => {
	const { hasWatermark = false } = opts;
	const runningHead = getRunningHead(title);
	const config = customConfig || getDefaultExportConfig(documentTypeName);

	const headerSectionHtml = generateSectionHtml(config.header, runningHead, 'header', title);
	const footerHtml = generateSectionHtml(config.footer, runningHead, 'footer', title);
	const watermarkHtml = hasWatermark ? generateWatermarkHtml() : '';
	const headerHtml = (headerSectionHtml || '') + watermarkHtml;

	const options = {
		format: 'A4',
		margin_top: '1in',
		margin_right: '1in',
		margin_bottom: '1in',
		margin_left: '1in',
		header_html: headerHtml || '',
		header_spacing: '0.5in',
	};

	if (footerHtml) {
		options.footer_html = footerHtml;
		options.footer_spacing = '0.5in';
	}

	return options;
};
